import { Autocomplete } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { ResponseAbortable } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import PmiTextField from 'shell/components/shared/text-field';
import { ItemVM } from 'shell/view-models/item';
import { Service } from 'shell/models/service';

const MinSearchLength = 2;

interface FormServiceCodeProps {
  selectedValue: ItemVM | null;
  handleChange: (value: ItemVM | null) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  error?: boolean;
  label?: string;
}

const ServiceCodeAutoComplete = ({
  selectedValue,
  handleChange,
  required,
  disabled,
  error,
  id = 'serviceCode',
  label = 'Codice Servizio',
}: FormServiceCodeProps): React.ReactElement => {
  const [services, getServices] = useHttpFetch<HttpResponseList<Service>, Array<ItemVM>>(
    `${__MANAGEMENT_API_URL__}/servizi`,
    {
      options: {
        method: 'GET',
        abortable: true,
        errorCatching: true,
        loading: false,
      },
      transformer: (response) => {
        return response.data?.payload?.list.map((service) => ({
          value: service.key.codiceServizio,
          id: service.key.codiceServizio,
        }));
      },
    }
  );

  const defaultProps = useMemo(() => {
    return {
      options: (services.data as Array<ItemVM>) || [],
      getOptionLabel: (option: ItemVM) => option.value,
    };
  }, [services.data]);

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        if (services.abort) {
          services.abort();
        }

        getServices({
          params: {},
          paramsSerializer: () => `codiceServizio=${value}`,
        }) as ResponseAbortable<HttpResponseList<Service>, Array<ItemVM>>;
      },
      [getServices, services.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      value={selectedValue}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      disabled={disabled}
      onInputChange={(event, value, reason) => {
        if (value.length >= MinSearchLength && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={services.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          error={error}
          label={required ? `${label} *` : label}
          variant="outlined"
        />
      )}
    />
  );
};

ServiceCodeAutoComplete.displayName = 'ServiceCode Auto Complete component';

export default ServiceCodeAutoComplete;
